import InfoOutlined from "@mui/icons-material/InfoOutlined";
import PersonAdd from "@mui/icons-material/PersonAdd";
import RemoveFilled from "@mui/icons-material/Remove";
import { Alert, Box, Stack, Tooltip, Typography } from "@mui/material";
import { GridActionsCellItem, GridColDef } from "@mui/x-data-grid-premium";
import { IssueCount } from "components/IssueCount";
import { useDueDateWarningsByUserId, useReviewerIssues } from "contexts/SubmissionFormContext";
import { Permission, SignatureType } from "generated-graphql/graphql";
import { useAuthorization } from "hooks/useAuthorization";
import { SubmissionReviewerSigner } from "hooks/useSubmissionForm";
import { useTenant } from "hooks/useTenant";
import { compact } from "lodash-es";
import { DateTime } from "luxon";
import { useMemo } from "react";
import { prettyPrintDate } from "util/dates";

import { DataGridWithAddButton } from "./DataGridWithAddButton";
import { renderIssueMessage } from "./helpers/renderIssueMessage";
import { SubmissionFormDatagrid } from "./SubmissionFormDatagrid";

type SignersDatagridProps = {
  signers: SubmissionReviewerSigner[];
  signatureType: SignatureType;
  handleAddSigner: () => void;
  handleRemoveSigner: (signer: SubmissionReviewerSigner) => void;
  submissionDueDate?: DateTime | null;
  disabled?: boolean;
};

export function SignersDatagrid({
  signers,
  signatureType,
  handleAddSigner,
  handleRemoveSigner,
  submissionDueDate,
  disabled = false,
}: SignersDatagridProps) {
  const issues = useReviewerIssues();
  const dueDateWarningsByUserId = useDueDateWarningsByUserId();
  const { hasPermissions } = useAuthorization();
  const { tenantId } = useTenant();

  const canManageUsers = hasPermissions([Permission.WriteAllUserTenant]);

  const signerIssues = useMemo(
    () =>
      issues.filter((i) =>
        signers.some((s) => i.schemaStack?.some((schemaStack) => schemaStack.name === s.id))
      ),
    [issues, signers]
  );
  const columns = useMemo(() => {
    const columns: GridColDef<SubmissionReviewerSigner>[] = [
      {
        field: "name",
        headerName: "Name",
        display: "flex",
        flex: 0.65,
        sortable: false,
      },
    ];

    if (signatureType === SignatureType.Digital) {
      columns.push({
        field: "email",
        headerName: "Email",
        display: "flex",
        sortable: false,
        flex: 1,
      });
    }

    if (signatureType === SignatureType.WetInk) {
      columns.push({
        field: "address",
        headerName: "Address",
        display: "flex",
        sortable: false,
        flex: 1,
        valueGetter: (_, row) => {
          return compact([
            compact([row.streetAddress1, row.streetAddress2, row.city]).join(" "),
            compact([row.state, row.zip]).join(" "),
            row.country,
          ]).join(", ");
        },
      });
    }

    columns.push({
      field: "dueDate",
      display: "flex",
      flex: 1,
      sortable: false,
      renderHeader: () => {
        const todayUtc = DateTime.utc();
        const earliestAllowed = todayUtc.plus({ days: 1 });
        return (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="body2" fontWeight={500}>
              Due Date
            </Typography>
            <Tooltip
              placement="right"
              title={`BoldSign requires due dates at least 1 day in the future based on UTC. Today's UTC date is ${todayUtc.toFormat("MMM d, yyyy")}, so the earliest allowed date is ${earliestAllowed.toFormat("MMM d, yyyy")}.`}
            >
              <InfoOutlined sx={{ fontSize: 16, color: "text.secondary" }} />
            </Tooltip>
          </Stack>
        );
      },
      renderCell: ({ row }) => {
        const warningMessage = row.id ? dueDateWarningsByUserId[row.id] : undefined;
        const dateStr = row.dueDate ? prettyPrintDate(row.dueDate.toISO()) : "";

        return (
          <Stack justifyContent="center" sx={{ height: "100%" }}>
            <Typography variant="body2">{dateStr}</Typography>
            {warningMessage && (
              <Typography variant="caption" sx={{ color: "warning.dark" }}>
                {warningMessage}
              </Typography>
            )}
          </Stack>
        );
      },
    });
    if (signerIssues.length > 0) {
      columns.push({
        field: "issues",
        display: "flex",
        flex: 0.6,
        headerName: "Issues",
        renderCell: ({ row }) => {
          const rowIssues = signerIssues.filter((i) => {
            return i.schemaStack?.some((s) => s.name === row.id);
          });
          return <IssueCount issueCount={rowIssues.length} />;
        },
      });
    }

    if (!disabled) {
      // Always add actions as the last column
      columns.push({
        field: "actions",
        type: "actions",
        headerName: "",
        width: 80,
        align: "right",
        sortable: false,
        getActions: ({ row }) => [
          <GridActionsCellItem
            key="delete"
            label="Delete"
            icon={<RemoveFilled />}
            onClick={() => {
              handleRemoveSigner(row);
            }}
          />,
        ],
      });
    }
    return columns;
  }, [signatureType, handleRemoveSigner, signerIssues, dueDateWarningsByUserId, disabled]);

  return (
    <Box>
      {signerIssues.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {signerIssues.map((i, index) => (
            <Typography key={index} variant="body2">
              {renderIssueMessage(i.message, canManageUsers, tenantId)}
            </Typography>
          ))}
        </Alert>
      )}
      {disabled ? (
        <SubmissionFormDatagrid rows={signers} columns={columns} />
      ) : (
        <DataGridWithAddButton
          rows={signers}
          columns={columns}
          addButtonConfig={{
            buttonText: "Add Signer",
            icon: <PersonAdd />,
            colSpan: 3,
          }}
          onAdd={handleAddSigner}
          createEmptyRow={() => ({
            id: "empty",
            name: "",
            email: "",
            streetAddress1: "",
            streetAddress2: "",
            city: "",
            state: "",
            zip: "",
            country: "",
            order: 0,
            isStaff: false,
          })}
          gridProps={{
            onRowClick: handleAddSigner,
          }}
        />
      )}
    </Box>
  );
}
